import * as React from 'react';

import {classify} from 'src/utils/classify';

import {DropIndicator} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/canvas/drop-indicator';
import type {IndicatorTarget} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/hooks/use-drag-indicator';
import layoutCss from './dashboard-builder-canvas-layout.css';


export type InvalidHoverReason =
  | {kind: 'type-mismatch'; rowType: string}
  | {kind: 'row-full'; rowType: string; max: number};

export type TileRowProps = {
  rowId: string;
  rowType: string;
  indicatorTarget: IndicatorTarget | null;
  isDropTarget: boolean;
  /**
   * When true, skip the inline vertical drop-indicator line. Used when a
   * cross-row tile-sized placeholder is rendered inside `tiles` — the
   * placeholder is the stronger visual, and rendering both reads as
   * double feedback at the same insertion point.
   */
  suppressInlineIndicator: boolean;
  isIncompatibleDropTarget: boolean;
  invalidHoverReason: InvalidHoverReason | null;
  showInvalidPill: boolean;
  pillLabel: string;
  onInvalidHover: (
    rowId: string | null,
    reason: InvalidHoverReason | null,
  ) => void;
  rowClassName: string;
  tiles: React.ReactNode;
  trailingContent: React.ReactNode;
};

/**
 * Renders one tile row's drop target with a positioned vertical drop
 * indicator. The indicator's offset is computed from the live DOM (each
 * draggable child's bounding box) so it lands between actual tile edges
 * rather than at a guessed pixel offset. Layout effect because the offset
 * must be measured from the same paint that renders the indicator —
 * useEffect would flash one frame at offset 0.
 */
export function TileRow({
  rowId,
  rowType,
  indicatorTarget,
  isDropTarget,
  suppressInlineIndicator,
  isIncompatibleDropTarget,
  invalidHoverReason,
  showInvalidPill,
  pillLabel,
  onInvalidHover,
  rowClassName,
  tiles,
  trailingContent,
}: TileRowProps) {
  const rowRef = React.useRef<HTMLDivElement | null>(null);
  const isTarget =
    indicatorTarget !== null &&
    indicatorTarget.droppableId === rowId &&
    indicatorTarget.orientation === 'vertical';
  const [offset, setOffset] = React.useState(0);

  React.useLayoutEffect(() => {
    if (!isTarget || !indicatorTarget || !rowRef.current) {
      return;
    }
    const parent = rowRef.current;
    const children = Array.from(parent.children).filter(
      (c) => c instanceof HTMLElement && c.hasAttribute('data-byod-tile'),
    ) as HTMLElement[];
    if (children.length === 0) {
      setOffset(0);
      return;
    }
    const parentRect = parent.getBoundingClientRect();
    const idx = indicatorTarget.index;
    const nextOffset =
      idx >= children.length
        ? children[children.length - 1].getBoundingClientRect().right -
          parentRect.left
        : children[idx].getBoundingClientRect().left - parentRect.left;
    setOffset(nextOffset);
  }, [isTarget, indicatorTarget]);

  const handleMouseEnter = React.useCallback(() => {
    if (isIncompatibleDropTarget && invalidHoverReason) {
      onInvalidHover(rowId, invalidHoverReason);
    }
  }, [isIncompatibleDropTarget, invalidHoverReason, onInvalidHover, rowId]);

  const handleMouseLeave = React.useCallback(() => {
    onInvalidHover(null, null);
  }, [onInvalidHover]);

  return (
    <div
      ref={rowRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={classify(
        rowClassName,
        isDropTarget && layoutCss.canvasRowDraggingOver,
        isIncompatibleDropTarget && layoutCss.canvasRowIncompatibleDropTarget,
      )}
      data-row-type={rowType}
    >
      {tiles}
      {trailingContent}
      {isTarget && !suppressInlineIndicator && (
        <DropIndicator orientation="vertical" offset={offset} />
      )}
      {showInvalidPill && invalidHoverReason && (
        <div
          className={layoutCss.invalidHintPill}
          role="status"
          aria-live="polite"
        >
          {pillLabel}
        </div>
      )}
    </div>
  );
}
